import { ClipboardList, FileText, Mic, Settings2, HelpCircle } from 'lucide-react';
/**
 * The interview lifecycle as a shared visual language.
 *
 * "Before / during / after the interview" is the spine the whole console reads
 * along, so it has to look the same everywhere: the same colour, the same icon
 * and the same order on a stat card, a stacked bar, a table row and a drill-down
 * page. Defining it once is what makes the dashboard learnable in one pass — an
 * operator learns "blue is before the interview" once, not per screen.
 *
 * ORDER IS FIXED AND IS NOT A RANKING. Sorting these by cost would destroy the
 * only thing the dimension says that a feature breakdown does not, which is
 * *when* the money was spent. The backend serves them in this order for the same
 * reason; this list mirrors `constants.PHASE_ORDER`.
 *
 * COLOUR FOLLOWS THE PHASE, NEVER ITS RANK. `pre_interview` is the same colour
 * whether it is the largest slice or absent, so filtering one phase out cannot
 * repaint the others.
 *
 * The hexes live in `index.css` as `--dash-phase-*` (two sets: one selected for
 * the dark surface, one for light) and are read through `var()` here, so the
 * theme toggle swaps them in one place. They are validated as a set for
 * colour-vision separation and contrast — see the comment beside the tokens.
 */
export const PHASE_ORDER = [
    'pre_interview',
    'in_interview',
    'post_interview',
    'platform',
    'unknown',
];
export const PHASE_STYLE = {
    pre_interview: {
        short: 'Setup',
        color: 'var(--dash-phase-pre)',
        icon: ClipboardList,
        chip: 'bg-dash-accentSoft text-dash-accentInk',
    },
    in_interview: {
        short: 'Live',
        color: 'var(--dash-phase-in)',
        icon: Mic,
        chip: 'bg-dash-warningSoft text-dash-warningInk',
    },
    post_interview: {
        short: 'Analysis',
        color: 'var(--dash-phase-post)',
        icon: FileText,
        chip: 'bg-dash-successSoft text-dash-successInk',
    },
    platform: {
        short: 'Platform',
        color: 'var(--dash-phase-platform)',
        icon: Settings2,
        chip: 'bg-dash-purpleSoft text-dash-purpleInk',
    },
    unknown: {
        short: 'Unclassified',
        color: 'var(--dash-phase-unknown)',
        icon: HelpCircle,
        chip: 'bg-dash-dangerSoft text-dash-dangerInk',
    },
};
const FALLBACK = {
    short: 'Other',
    color: 'var(--dash-phase-unknown)',
    icon: HelpCircle,
    chip: 'bg-dash-active text-dash-text2',
};
export const phaseStyle = (phase) => { var _a; return (_a = PHASE_STYLE[phase !== null && phase !== void 0 ? phase : '']) !== null && _a !== void 0 ? _a : FALLBACK; };
/**
 * Sort anything phase-keyed back into lifecycle order.
 *
 * The API already serves phases ordered, but a client-side `map`/`filter` over a
 * record or a `Object.entries` walk loses it — this puts it back rather than
 * letting a screen quietly render "after, before, during".
 */
export const byPhaseOrder = (rows, key) => [...rows].sort((a, b) => {
    const ai = PHASE_ORDER.indexOf(key(a));
    const bi = PHASE_ORDER.indexOf(key(b));
    return (ai < 0 ? PHASE_ORDER.length : ai) - (bi < 0 ? PHASE_ORDER.length : bi);
});
/**
 * The eight categorical series slots, in fixed assignment order.
 *
 * Read from CSS variables so light and dark get their own selected steps. A
 * chart takes `SERIES_COLORS[i]` for its i-th series and NEVER wraps with a
 * modulo: past eight, fold the tail into "Other" (see `topWithOther`). Cycling
 * would paint two different series the same colour, which reads as one series.
 */
export const SERIES_COLORS = [
    'var(--dash-series-1)',
    'var(--dash-series-2)',
    'var(--dash-series-3)',
    'var(--dash-series-4)',
    'var(--dash-series-5)',
    'var(--dash-series-6)',
    'var(--dash-series-7)',
    'var(--dash-series-8)',
];
/**
 * Slot for series `i`.
 *
 * `topWithOther` guarantees no chart ever asks for more slots than exist, so a
 * clamp here is unreachable in practice and deliberately NOT a wrap: repeating
 * slot 1 would paint two different series identically and hide the bug, whereas
 * clamping makes an over-long series list visibly wrong at the tail.
 */
export const seriesColor = (i) => SERIES_COLORS[Math.min(i, SERIES_COLORS.length - 1)];
